/**
 * The support line under a refusal: the reference id a person can quote, and the
 * machine code they cannot.
 *
 * The finish review's rule: `WORKFORCE_SCOPE_DENIED` is vocabulary from the wire, not
 * from the reader's language, and printing it in body copy makes a working refusal
 * read as a crash. The code still has to reach support, so it stays in the DOM in two
 * places a person never sees — a `data-error-code` attribute and one
 * `wp-visually-hidden` span — and the only line on the paper is
 * "Mã tra cứu: <correlation id>".
 *
 * One component rather than the ten copies of this paragraph that existed before it,
 * because the next screen that hand-rolls it will print the enum again.
 */

import type { ReactNode } from 'react';

import { useT } from '../i18n';

export interface ErrorCodeLineProps {
  /** The `WORKFORCE_*` enum. Never rendered visibly. */
  readonly code: string;
  readonly correlationId?: string | null;
}

export function ErrorCodeLine({ code, correlationId = null }: ErrorCodeLineProps): ReactNode {
  const t = useT();
  return (
    <p className="wp-small wp-muted" data-error-code={code}>
      <span className="wp-visually-hidden">
        {t('common.errorCode')}: {code}
      </span>
      {correlationId === null ? null : (
        <>
          {t('common.correlationId')}: <code>{correlationId}</code>
        </>
      )}
    </p>
  );
}
